import {Records} from "@buildwithflux/shared";
import {shallow} from "zustand/shallow";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "./types";

/**
 * Creates a selector that gets an item by UID from a map
 * @param mapSelector Selector that returns the map
 * @returns Parameterized selector function
 */
export const createByUidSelector = <TValue>(
    mapSelector: DocumentSelector<Record<string, TValue> | undefined>,
): ParameterizedSelector<string, TValue | undefined> => {
    return (uid: string) => (state: DocumentStoreState) => mapSelector(state)?.[uid];
};

/**
 * Creates a selector that gets items by multiple UIDs from a map
 * @param mapSelector Selector that returns the map
 * @returns Parameterized selector function
 */
export const createByUidsSelector = <T extends Record<string, unknown>>(
    mapSelector: DocumentSelector<T | undefined>,
): ParameterizedSelector<string[], Array<NonNullable<T[string]>>> => {
    return (uids: string[]) => (state: DocumentStoreState) => {
        const map = mapSelector(state);
        if (!map) return [];
        const result: Array<NonNullable<T[string]>> = [];
        for (const uid of uids) {
            const item = map[uid];
            if (item !== undefined) {
                result.push(item as NonNullable<T[string]>);
            }
        }
        return result;
    };
};

/**
 * Creates a selector that counts items in a map
 * @param mapSelector Selector that returns the map
 * @returns Count selector
 */
export const createCountSelector = <T extends Record<string, unknown>>(
    mapSelector: DocumentSelector<T | undefined>,
): DocumentSelector<number> => {
    return (state: DocumentStoreState) => {
        const map = mapSelector(state);
        if (!map) return 0;
        return Records.count(map);
    };
};

/**
 * Creates a selector that checks if a map is empty
 * @param mapSelector Selector that returns the map
 * @returns Boolean selector
 */
export const createIsEmptySelector = <T extends Record<string, unknown>>(
    mapSelector: DocumentSelector<T | undefined>,
): DocumentSelector<boolean> => {
    return (state: DocumentStoreState) => {
        const map = mapSelector(state);
        if (!map) return true;
        return Records.isEmpty(map);
    };
};

/**
 * Creates a selector that gets all keys from a map
 * @param mapSelector Selector that returns the map
 * @returns Keys selector
 */
export const createKeysSelector = <T extends Record<string, unknown>>(
    mapSelector: DocumentSelector<T | undefined>,
): DocumentSelector<string[]> => {
    return (state: DocumentStoreState) => {
        const map = mapSelector(state);
        if (!map) return [];
        return Object.keys(map);
    };
};

/**
 * Creates a selector that gets all values from a map
 * @param mapSelector Selector that returns the map
 * @returns Values selector
 */
export const createValuesSelector = <T extends Record<string, unknown>>(
    mapSelector: DocumentSelector<T | undefined>,
): DocumentSelector<Array<NonNullable<T[string]>>> => {
    return (state: DocumentStoreState) => {
        const map = mapSelector(state);
        if (!map) return [];
        const result: Array<NonNullable<T[string]>> = [];
        for (const value of Records.iterate(map)) {
            if (value !== undefined) {
                result.push(value as NonNullable<T[string]>);
            }
        }
        return result;
    };
};

/**
 * Default shallow equality checker for Zustand
 * Use this with useShallow for object/array selectors to prevent unnecessary re-renders
 */
export const defaultEquality = shallow;

/**
 * Empty array constant to avoid creating new arrays on every render
 * Use this as a fallback for array selectors
 */
export const emptyArray: string[] = [];

/**
 * Empty object constant to avoid creating new objects on every render
 * Use this as a fallback for object selectors
 */
export const emptyObject: Record<string, never> = {};
